import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Session } from '@utils/types/editors/sessions.ts';
import { WaveformLiveRegion } from './waveform-live-region';
import { waveInfoErrorKey } from './wave-info-error.ts';

type Props = {
  isCaptionEditor?: boolean;
  leftOffset?: string;
  rightOffset?: string;
  session: Session | undefined;
};

export const AudioWaveform: React.FC<Props> = ({
  isCaptionEditor = false,
  session
}: Props) => {
  const { t } = useTranslation();
  // Caption editor: 110px content -> 92px container = 18px to remove (13px top, 5px bottom)
  // Transcript editor: 110px content -> 80px container = 30px to remove (15px each side to center)
  const cropFromTop = isCaptionEditor ? 13 : 15;
  const cropFromBottom = isCaptionEditor ? 5 : 15;

  const status = session?.wave_info_status ?? null;
  // While the worker is producing the new waveform, or after a source swap
  // that has not been regenerated yet, `wave_info_url` is intentionally
  // null (see backend: wave_info_sas_url refuses to hand out stale bytes).
  // Render an in-place notice so seeking degrades gracefully instead of
  // the whole strip disappearing.
  const isPending = status === 'pending' || status === 'running';
  const isStale = status === 'stale';
  const isFailed = status === 'failed';

  if (!session?.wave_info_url) {
    if (!isPending && !isStale && !isFailed) return null;

    let message = '';
    if (isPending) message = t('wave-info-regenerating-notice');
    else if (isStale) message = t('wave-info-stale-notice');
    else if (isFailed) message = t(waveInfoErrorKey(session?.wave_info_error));

    return (
      <div
        className="waveform-container overflow-hidden flex items-center justify-center"
        role="region"
        aria-label={t('audio-waveform') || 'Audio waveform'}
        aria-live="polite"
        style={{
          height: isCaptionEditor ? '70px' : '72px',
          fontSize: '11px',
          opacity: 0.75,
          padding: '0 12px',
          textAlign: 'center'
        }}
      >
        {message}
      </div>
    );
  }

  return (
    <div
      className="waveform-container overflow-hidden"
      // role + aria-label so AT users can find the waveform region in the
      // landmark/region navigation; aria-hidden on the canvas itself happens
      // implicitly (peaks.js renders to <canvas> with no accessible name).
      // The textual alternative lives in <WaveformLiveRegion />.
      role="region"
      aria-label={t('audio-waveform') || 'Audio waveform'}
      style={{
        height: isCaptionEditor ? '70px' : '72px'
      }}
    >
      <WaveformLiveRegion />
      <motion.div
        id="peaks-overview-container"
        aria-hidden="true"
        style={{
          height: '88px',
          marginTop: `-${cropFromTop}px`,
          marginBottom: `-${cropFromBottom}px`,
          paddingLeft: '1px',
          paddingRight: '1px'
        }}
      ></motion.div>
    </div>
  );
};
